// Capability gates for the logistics-requests UI. The transition/action
// state-machine and the shared party-authority gate live in
// `./role.transitions.ts`; re-exported below so the `./role.js` surface is
// unchanged.

import { RequestStatus, hasPermission, type RequestKind } from '@magimp/shared';
import type { PermissionsBundle } from '../../stores/auth.js';
import { isPartyAllowed, type ActorPartyContext } from './role.transitions.js';

// WHY (#1748): `detectRoleKind`, `roleSubtitleKey`/`RoleSubtitleKey`, and
// `canCreateRequest` moved into the narrow request-display capability
// (`./display/role.js`); re-exported here so existing `from './role.js'`
// consumers and the feature barrel resolve them unchanged.
export { detectRoleKind, roleSubtitleKey, canCreateRequest } from './display/role.js';
export type { RoleSubtitleKey } from './display/role.js';

// re-export the public transition surface so `./role.js` stays byte-identical:
export { allowedActions } from './role.transitions.js';
export type {
  RequestFacts,
  TransitionLabelKey,
  TransitionDisabledReasonKey,
  TransitionAction,
  ActorPartyContext,
} from './role.transitions.js';

// INVARIANT: mirrors the backend's `pickWinner` capability gate end to
// end. The check is two-layered on the backend:
//   1. Controller (`apps/backend/src/modules/requests/requests.controller.ts`,
//      `POST /requests/:id/winner`) is `@RequiresPermission('requests.
//      manage')`.
//   2. Service (`request-winner.service.ts:pickWinner`) is admin OR
//      (managerId match AND `canAuthorRequest`).
// INVARIANT (#1164): the status window covers IN_WORK AND PROCESSED.
//   The logist hands the row off to the manager via `process` (gated
//   only on `answered ≥ 1`); the manager then owns the winner decision
//   in PROCESSED — either from the carriers tab CTA or inside the
//   success modal, which commits `pickWinner` + `success` as a pair.
//   `reopen` PROCESSED → IN_WORK still clears `winnerBidId` as
//   defense-in-depth.
// INVARIANT (#1836): the window also covers REFUSED and POSTPONED so the
//   manager can revive such a row to SUCCESS by re-picking a winner. A
//   refused row always cleared its winner, so `hasWinner` is false there; a
//   postponed row preserves its winner, so the `hasWinner` guard hides the
//   picker until the stored winner is gone — both mirror the backend
//   `pickWinner` status window.
export function canPickWinner(
  permissions: PermissionsBundle,
  status: RequestStatus,
  hasWinner: boolean,
  party: ActorPartyContext,
): boolean {
  if (hasWinner) return false;
  if (
    status !== RequestStatus.IN_WORK &&
    status !== RequestStatus.PROCESSED &&
    status !== RequestStatus.REFUSED &&
    status !== RequestStatus.POSTPONED
  ) {
    return false;
  }
  if (!hasPermission(permissions, 'requests.manage')) return false;
  return isPartyAllowed('manager', permissions, party);
}

export function canManageRequestCarriers(
  permissions: PermissionsBundle,
  status: RequestStatus,
  party: ActorPartyContext,
): boolean {
  if (status !== RequestStatus.IN_WORK) return false;
  if (!hasPermission(permissions, 'requests.manage')) return false;
  return isPartyAllowed('logist', permissions, party);
}

// INVARIANT (#2137/#2139): on a CANNOT_TRANSPORT row the assigned logist
//   (or admin) may drive the MINIMAL revive path #2137 widened on the
//   backend — add a carrier, reset a REJECTED carrier to PENDING, and add a
//   bid (the first answer auto-revives the row to IN_WORK). Bid edit/delete
//   and carrier delete stay closed; this mirrors the backend assert* guards
//   so the logist never sees a control the server would reject.
export function canReviveCarriersOnCannotTransport(
  permissions: PermissionsBundle,
  status: RequestStatus,
  party: ActorPartyContext,
): boolean {
  if (status !== RequestStatus.CANNOT_TRANSPORT) return false;
  if (!hasPermission(permissions, 'requests.manage')) return false;
  return isPartyAllowed('logist', permissions, party);
}

/**
 * @aiPurpose client-side visibility gate for the regroup edit toggle
 *   (#1178, §6). `true` when the actor is the assigned logist (or
 *   admin bypass) AND the request is `IN_WORK` AND the kind is not
 *   `CONTAINER` (one item per container — nothing to regroup) AND the
 *   parent box exposes its `updatedAt` token (only emitted under
 *   `?includeBoxVersions=true`). Mirrors the backend chokepoint
 *   `RequestsAccessService.assertCanRegroupBoxItems`; the 8-status
 *   gate and the cross-box bid/insurance override-conflict filter
 *   stay on the server.
 * @aiSecurity advisory only — the backend re-applies the gate and the
 *   404-not-403 concealment for visible-but-unassigned logists. A
 *   flipped client predicate cannot grant access; it only hides the UI
 *   to prevent the foreseeable 403/404 round-trip and to keep stale
 *   pre-#1178 bundles in read-only mode.
 */
export function canRegroupBoxItems(
  permissions: PermissionsBundle,
  status: RequestStatus,
  kind: RequestKind,
  party: ActorPartyContext,
  boxUpdatedAt: string | undefined,
): boolean {
  if (status !== RequestStatus.IN_WORK) return false;
  if (kind === 'CONTAINER') return false;
  if (boxUpdatedAt === undefined) return false;
  if (!hasPermission(permissions, 'requests.manage')) return false;
  return isPartyAllowed('logist', permissions, party);
}

// INVARIANT (#962): mirrors the backend's first-carrier window in
//   READY_FOR_WORK. Backend `CarriersController.addCarrier` accepts
//   adds from logist-side actors while the row is in READY_FOR_WORK,
//   and the first successful add auto-transitions the row to IN_WORK
//   (#961). The UI mirrors that single-action window so the
//   "Add carrier" affordance shows in READY_FOR_WORK, and only once —
//   the moment the request transitions to IN_WORK, the broader
//   `canManageRequestCarriers` gate (bid edit / delete / etc.) takes
//   over.
export function canAddFirstCarrier(
  permissions: PermissionsBundle,
  status: RequestStatus,
  carrierCount: number,
  party: ActorPartyContext,
): boolean {
  if (status !== RequestStatus.READY_FOR_WORK) return false;
  if (carrierCount > 0) return false;
  if (!hasPermission(permissions, 'requests.manage')) return false;
  return isPartyAllowed('logist', permissions, party);
}

/**
 * @aiPurpose client-side visibility gate for the Duplicate action on
 *   the China drawer header (#2119). `true` for the request OWNER (the
 *   actor is the row `managerId` AND holds the author-identity capability)
 *   OR admin — mirroring the backend owner-or-admin gate in
 *   `RequestsDuplicateService`. Status-blind (any source status may be
 *   duplicated); the affordance is hidden for non-owners (a `scope.all`
 *   manager / assigned logist) so they never see an action the backend
 *   would 403.
 * @aiSecurity advisory only — the backend chokepoint re-applies the gate.
 *   A flipped client predicate cannot grant access; it only hides the UI.
 */
export function canDuplicateRequest(
  permissions: PermissionsBundle,
  party: ActorPartyContext,
): boolean {
  return isPartyAllowed('manager', permissions, party);
}

/**
 * @aiPurpose client-side visibility gate for the Russia-rate button on
 *   the China drawer header (#707). Returns `true` when the actor holds
 *   `russiaRequests.manage` (admin bypass via `permissions === 'ALL'`).
 *   Cargo presence (items + boxes count) is signalled separately via
 *   {@link hasParentCargo} so the button renders disabled with a
 *   tooltip when the parent is empty, instead of vanishing — managers
 *   need to see the affordance to learn it exists.
 * @aiSecurity advisory only — the backend chokepoint
 *   (`apps/backend/src/modules/russia-requests/from-parent.service.ts`)
 *   re-applies the gate plus the owner-only parent check. A flipped
 *   client predicate cannot grant access.
 */
export function canCreateRussiaRequestFromParent(permissions: PermissionsBundle): boolean {
  return hasPermission(permissions, 'russiaRequests.manage');
}

/**
 * @aiPurpose disabled-state predicate for the #707 button. `true` when
 *   the parent China request has at least one item or one box. Boxes
 *   are optional on legacy list reads (`r.boxes?.length ?? 0`) so the
 *   helper coerces both counts via `?? 0`.
 */
export function hasParentCargo(facts: {
  itemCount: number;
  boxCount: number | undefined;
}): boolean {
  return (facts.itemCount ?? 0) + (facts.boxCount ?? 0) > 0;
}
